import { useEffect, useState, type FormEvent } from 'react';
import { toast } from 'sonner';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Switch } from '@/components/ui/Switch';
import { useCreateContact, useUpdateContact, type SaveContactInput } from '@/hooks/useClients';
import type { Contact } from '@/types/clients';

type Props = {
  open: boolean;
  onClose: () => void;
  clientId: string;
  contact?: Contact | null;
};

const empty = (clientId: string): SaveContactInput => ({
  clientId, firstName: '', lastName: '', title: '',
  email: '', phone: '', mobile: '', isPrimary: false, notes: '',
});

export function ContactFormModal({ open, onClose, clientId, contact }: Props) {
  const create = useCreateContact();
  const update = useUpdateContact();
  const [form, setForm] = useState<SaveContactInput>(empty(clientId));
  const [error, setError] = useState<string | null>(null);
  const editing = !!contact;

  useEffect(() => {
    if (open) {
      setForm(contact
        ? { ...empty(clientId), ...contact }
        : empty(clientId),
      );
      setError(null);
    }
  }, [open, contact, clientId]);

  function update_<K extends keyof SaveContactInput>(key: K, val: SaveContactInput[K]) {
    setForm((f) => ({ ...f, [key]: val }));
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    setError(null);
    try {
      if (editing && contact) {
        await update.mutateAsync({ id: contact.id, ...form });
        toast.success('Contact updated');
      } else {
        await create.mutateAsync(form);
        toast.success('Contact added');
      }
      onClose();
    } catch (err) {
      setError((err as { message?: string }).message ?? 'Save failed.');
    }
  }

  const submitting = create.isPending || update.isPending;

  return (
    <Modal
      open={open}
      onClose={onClose}
      kicker={editing ? 'Edit Contact' : 'New Contact'}
      title={editing ? `${contact?.firstName} ${contact?.lastName}` : 'Add a contact.'}
      footer={
        <>
          <Button variant="ghost" onClick={onClose} disabled={submitting}>Cancel</Button>
          <Button type="submit" form="contact-form" loading={submitting}>
            {editing ? 'Save' : 'Add Contact'}
          </Button>
        </>
      }
    >
      <form id="contact-form" onSubmit={onSubmit} className="space-y-5 py-2">
        <div className="grid grid-cols-2 gap-4">
          <Input label="First name" required value={form.firstName} onChange={(e) => update_('firstName', e.target.value)} />
          <Input label="Last name"  required value={form.lastName}  onChange={(e) => update_('lastName',  e.target.value)} />
        </div>

        <Input label="Title" placeholder="e.g. Senior Specifier" value={form.title ?? ''} onChange={(e) => update_('title', e.target.value)} />

        <div className="grid grid-cols-2 gap-4">
          <Input label="Email"  type="email" value={form.email ?? ''} onChange={(e) => update_('email', e.target.value)} />
          <Input label="Phone"  value={form.phone ?? ''}  onChange={(e) => update_('phone',  e.target.value)} />
          <Input label="Mobile" value={form.mobile ?? ''} onChange={(e) => update_('mobile', e.target.value)} className="col-span-2" />
        </div>

        <div className="pt-3 border-t border-border">
          <Switch
            checked={form.isPrimary ?? false}
            onChange={(v) => update_('isPrimary', v)}
            label="Primary contact"
            description="Marks this contact as the main point of communication for the account."
          />
        </div>

        {error && (
          <div className="text-xs text-danger border-l-2 border-danger pl-3 py-1">{error}</div>
        )}
      </form>
    </Modal>
  );
}
